import { createContext, useContext } from 'react';
import type { LiveDocumentEditingConfig } from '@/document-render/presentation/editing/quote/LiveDocumentEditingConfig';
import type { ProseEditorCloseOptions, RenderProseEditor } from '@/document-render/presentation/editing/proseEditorSlot';
import { isQuoteEditableKind } from '@/document-render/presentation/editing/quote/quoteEditableKinds';

/**
 * The quote canvas's in-place prose editor (LLAMS-610 DB-05): present only when the mount supplied
 * an editor slot. `isEditing` is shared by every page slice of one block instance.
 */
export interface QuoteProseEditorState {
  render: RenderProseEditor;
  isEditing: boolean;
  start: () => void;
  stop: (options?: ProseEditorCloseOptions) => void;
}

export type QuoteCanvasSlice =
  | {
      kind: 'prose';
      onEditProse: NonNullable<LiveDocumentEditingConfig['onEditProse']>;
      proseEditor?: QuoteProseEditorState;
    }
  | { kind: 'lineItems'; editing: LiveDocumentEditingConfig }
  | { kind: 'roles'; editing: LiveDocumentEditingConfig }
  | { kind: 'signature'; editing: LiveDocumentEditingConfig }
  | { kind: 'priceBook'; editing: LiveDocumentEditingConfig };

const QuoteCanvasEditingContext = createContext<QuoteCanvasSlice | null>(null);

export function buildQuoteCanvasSlice(kind: string, editing: LiveDocumentEditingConfig | undefined): QuoteCanvasSlice | null {
  if (!editing || !isQuoteEditableKind(kind, editing)) return null;
  if (kind === 'price_book') return { kind: 'priceBook', editing };
  if (kind === 'line_items_table') return { kind: 'lineItems', editing };
  if (kind === 'roles_responsibilities') return { kind: 'roles', editing };
  if (kind === 'signature') return { kind: 'signature', editing };
  if (editing.onEditProse) return { kind: 'prose', onEditProse: editing.onEditProse };
  return null;
}

export function useQuoteCanvasEditing(): QuoteCanvasSlice | null {
  return useContext(QuoteCanvasEditingContext);
}

export { QuoteCanvasEditingContext };
